import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Table } from 'react-bootstrap';
import { DIAS, calcularInicioContagem, deMinutos, paraMinutos, turnosValidos } from '../utils/janelaDescarga';

const SIMULACAO_INICIAL = { diaIndice: 5, hora: '15:20' };

// Casos de referência da regra: [rótulo, índice do dia (0 = segunda), hora]
const CASOS = [
  { rotulo: 'Sábado depois das 14h', diaIndice: 5, hora: '15:20' },
  { rotulo: 'Domingo', diaIndice: 6, hora: '10:00' },
  { rotulo: 'Dia útil depois das 14h', diaIndice: 2, hora: '16:30' },
  { rotulo: 'Sexta depois das 14h', diaIndice: 4, hora: '18:00' },
  { rotulo: 'Antes de abrir', diaIndice: 1, hora: '06:15' },
  { rotulo: 'Perto do meio-dia', diaIndice: 1, hora: '11:30' },
  { rotulo: 'Dentro da janela', diaIndice: 1, hora: '09:40' }
];

// 2380 -> '39h40'; 45 -> '45min'; 780 -> '13h'
const formatarDuracao = (minutos) => {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto}min`;
  return resto === 0 ? `${horas}h` : `${horas}h${String(resto).padStart(2, '0')}`;
};

const tituloResultado = (resultado, modo) => {
  if (!resultado) return 'Sem turno configurado';
  const momento = `${DIAS[resultado.diaIndice]}, ${deMinutos(resultado.minutos)}`;
  if (modo === 'CHEGADA') return `Na chegada — ${momento}`;
  return resultado.semanaSeguinte ? `${momento} (semana seguinte)` : momento;
};

const turnoDaChegada = (rascunho, resultado) => {
  const turnos = turnosValidos(rascunho.dias[resultado.diaIndice]) ?? [];
  return turnos.find((turno) => turno.inicio <= resultado.minutos && resultado.minutos < turno.fim);
};

const detalheResultado = (rascunho, resultado) => {
  if (!resultado) return 'Ative ao menos um dia de recebimento ou revise os turnos.';
  if (rascunho.modoForaJanela === 'CHEGADA') return 'Esta janela conta desde a chegada, sem considerar os turnos.';
  if (resultado.dentroDoTurno) {
    const turno = turnoDaChegada(rascunho, resultado);
    return turno
      ? `Chegou dentro do turno ${deMinutos(turno.inicio)}–${deMinutos(turno.fim)}.`
      : 'Chegou dentro do turno.';
  }
  return `Chegou fora da janela: ${formatarDuracao(resultado.esperaForaMinutos)} até a abertura não entram no tempo de espera.`;
};

const JanelaSimuladorSection = ({ rascunho }) => {
  const [simulacao, setSimulacao] = useState(SIMULACAO_INICIAL);

  const horaInformada = paraMinutos(simulacao.hora) !== null;
  const resultado = calcularInicioContagem(rascunho, simulacao.diaIndice, simulacao.hora);

  return (
    <section className="mb-4">
      <h6 className="mb-1">Simular chegada</h6>
      <p className="text-muted small mb-3">
        Confira quando o tempo de espera começa a contar com a configuração acima.
      </p>

      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <Form.Group controlId="janela-simulador-dia">
          <Form.Label className="small mb-1">Dia da chegada</Form.Label>
          <Form.Select
            size="sm"
            value={simulacao.diaIndice}
            onChange={(event) => setSimulacao((atual) => ({ ...atual, diaIndice: Number(event.target.value) }))}
          >
            {DIAS.map((dia, indice) => <option key={dia} value={indice}>{dia}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="janela-simulador-hora">
          <Form.Label className="small mb-1">Hora</Form.Label>
          <Form.Control
            type="time"
            size="sm"
            value={simulacao.hora}
            onChange={(event) => setSimulacao((atual) => ({ ...atual, hora: event.target.value }))}
          />
        </Form.Group>
      </div>

      <div className="border rounded p-3 mb-3 bg-light" role="status">
        <div className="small text-muted">Tempo de espera começa a contar</div>
        <div className="fs-5 fw-semibold">
          {horaInformada ? tituloResultado(resultado, rascunho.modoForaJanela) : 'Informe a hora da chegada'}
        </div>
        {horaInformada ? <div className="small">{detalheResultado(rascunho, resultado)}</div> : null}
      </div>

      <Table size="sm" responsive className="mb-0 align-middle">
        <caption className="caption-top small">Casos de referência — clique para simular</caption>
        <thead>
          <tr>
            <th>Caso de referência</th>
            <th>Chegada</th>
            <th>Começa a contar</th>
            <th>Fora da janela</th>
            <th aria-label="Ação" />
          </tr>
        </thead>
        <tbody>
          {CASOS.map((caso) => {
            const calculado = calcularInicioContagem(rascunho, caso.diaIndice, caso.hora);
            return (
              <tr key={caso.rotulo}>
                <td>{caso.rotulo}</td>
                <td>{`${DIAS[caso.diaIndice]} ${caso.hora}`}</td>
                <td>{tituloResultado(calculado, rascunho.modoForaJanela)}</td>
                <td>{calculado?.esperaForaMinutos ? formatarDuracao(calculado.esperaForaMinutos) : '—'}</td>
                <td className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setSimulacao({ diaIndice: caso.diaIndice, hora: caso.hora })}
                  >
                    Simular
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </section>
  );
};

JanelaSimuladorSection.propTypes = {
  rascunho: PropTypes.shape({
    modoForaJanela: PropTypes.string.isRequired,
    dias: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired
};

export default JanelaSimuladorSection;
